import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { UserResponse, BodyFatMeasurementResponse } from '../types/api';
import { useTranslation } from '../context/LanguageContext';
import { useConfirmation } from '../hooks/useConfirmation';
import ConfirmationDialog from './ConfirmationDialog';
import './Modal.css';

interface BodyFatMeasurementsModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: UserResponse | null;
}

const formatDateDDMMYYYY = (dateStr: string): string => {
  if (!dateStr) return '';
  // Handle both "YYYY-MM-DD" and "YYYY-MM-DDT..." formats
  const datePart = dateStr.split('T')[0];
  const parts = datePart.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

const BodyFatMeasurementsModal: React.FC<BodyFatMeasurementsModalProps> = ({
  isOpen,
  onClose,
  client
}) => {
  const { t } = useTranslation();
  const { confirmation, showConfirmation, hideConfirmation } = useConfirmation();

  const [measurements, setMeasurements] = useState<BodyFatMeasurementResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add new measurement state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMeasurementDate, setNewMeasurementDate] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Edit latest measurement state
  const [isEditingLatest, setIsEditingLatest] = useState(false);
  const [editDate, setEditDate] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchMeasurements = useCallback(async () => {
    if (!client) return;
    setIsLoading(true);
    setError('');
    try {
      const response = await apiService.getClientBodyFatMeasurements(client.id);
      if (response.success && response.data) {
        setMeasurements(response.data);
      } else {
        setMeasurements([]);
      }
    } catch (err: any) {
      console.error('Error fetching measurements:', err);
      // If 404, it just means no measurements yet
      if (err?.response?.status === 404) {
        setMeasurements([]);
      } else {
        setError(t('failedToLoadMeasurements'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [client, t]);

  useEffect(() => {
    if (isOpen && client) {
      fetchMeasurements();
      // Reset states
      setShowAddForm(false);
      setIsEditingLatest(false);
      setNewMeasurementDate('');
      setEditDate('');
      setSuccess('');
      setError('');
    }
  }, [isOpen, client, fetchMeasurements]);

  const handleCreate = async () => {
    if (!client || !newMeasurementDate) return;
    setIsCreating(true);
    setError('');
    setSuccess('');
    try {
      const response = await apiService.createBodyFatMeasurement(client.id, {
        measurementDate: newMeasurementDate
      });
      if (response.success) {
        setSuccess(t('measurementCreatedSuccessfully'));
        setShowAddForm(false);
        setNewMeasurementDate('');
        await fetchMeasurements();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(t('failedToCreateMeasurement'));
      }
    } catch (err: any) {
      console.error('Error creating measurement:', err);
      setError(err?.response?.data?.message || t('failedToCreateMeasurement'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateLatest = async () => {
    if (!client || !editDate || measurements.length === 0) return;
    const latest = measurements[0];
    setIsUpdating(true);
    setError('');
    setSuccess('');
    try {
      const response = await apiService.updateBodyFatMeasurement(client.id, latest.id, {
        measurementDate: editDate
      });
      if (response.success) {
        setSuccess(t('measurementUpdatedSuccessfully'));
        setIsEditingLatest(false);
        await fetchMeasurements();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(t('failedToUpdateMeasurement'));
      }
    } catch (err: any) {
      console.error('Error updating measurement:', err);
      setError(err?.response?.data?.message || t('failedToUpdateMeasurement'));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = (measurement: BodyFatMeasurementResponse) => {
    showConfirmation({
      title: t('deleteMeasurement'),
      message: t('confirmDeleteMeasurement'),
      type: 'danger',
      confirmText: t('delete'),
      cancelText: t('cancel'),
      onConfirm: async () => {
        hideConfirmation();
        try {
          const response = await apiService.deleteBodyFatMeasurement(client!.id, measurement.id);
          if (response.success) {
            setSuccess(t('measurementDeletedSuccessfully'));
            await fetchMeasurements();
            setTimeout(() => setSuccess(''), 3000);
          } else {
            setError(t('failedToDeleteMeasurement'));
          }
        } catch (err: any) {
          console.error('Error deleting measurement:', err);
          setError(err?.response?.data?.message || t('failedToDeleteMeasurement'));
        }
      },
      onCancel: hideConfirmation
    });
  };

  const startEditLatest = () => {
    if (measurements.length === 0) return;
    const latest = measurements[0];
    setEditDate(latest.measurementDate.split('T')[0]);
    setIsEditingLatest(true);
  };

  if (!isOpen) return null;

  const latest = measurements.length > 0 ? measurements[0] : null;
  const history = measurements.length > 1 ? measurements.slice(1) : [];

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{t('bodyFatMeasurements')} - {client?.name}</h2>
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>

          <div className="modal-body" style={{ padding: '24px' }}>
            {error && <div className="error-message" style={{ marginBottom: '16px', padding: '12px', background: '#fee2e2', color: '#dc2626', borderRadius: '8px' }}>{error}</div>}
            {success && <div className="success-message" style={{ marginBottom: '16px', padding: '12px', background: '#dcfce7', color: '#16a34a', borderRadius: '8px' }}>{success}</div>}

            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>{t('loading')}</div>
            ) : (
              <>
                {/* Add measurement button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                  {!showAddForm ? (
                    <button
                      onClick={() => setShowAddForm(true)}
                      style={{
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                    >
                      <span style={{ fontSize: '18px', lineHeight: '1' }}>+</span>
                      {t('addMeasurement')}
                    </button>
                  ) : (
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                      background: '#f8fafc',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      width: '100%'
                    }}>
                      <label style={{ fontWeight: 500, fontSize: '14px', whiteSpace: 'nowrap' }}>
                        {t('measurementDate')}:
                      </label>
                      <input
                        type="date"
                        value={newMeasurementDate}
                        onChange={(e) => setNewMeasurementDate(e.target.value)}
                        style={{
                          padding: '6px 10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '14px',
                          flex: 1
                        }}
                      />
                      <button
                        onClick={handleCreate}
                        disabled={!newMeasurementDate || isCreating}
                        style={{
                          background: '#16a34a',
                          color: 'white',
                          border: 'none',
                          padding: '6px 14px',
                          borderRadius: '4px',
                          cursor: newMeasurementDate && !isCreating ? 'pointer' : 'not-allowed',
                          fontSize: '14px',
                          fontWeight: 500,
                          opacity: !newMeasurementDate || isCreating ? 0.6 : 1
                        }}
                      >
                        {isCreating ? t('saving') : t('save')}
                      </button>
                      <button
                        onClick={() => { setShowAddForm(false); setNewMeasurementDate(''); }}
                        style={{
                          background: '#6b7280',
                          color: 'white',
                          border: 'none',
                          padding: '6px 14px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        {t('cancel')}
                      </button>
                    </div>
                  )}
                </div>

                {/* Latest Measurement Section */}
                <div style={{
                  background: '#f8fafc',
                  border: '2px solid #dc2626',
                  borderRadius: '10px',
                  padding: '20px',
                  marginBottom: '24px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, color: '#1a202c', fontSize: '16px', fontWeight: 600 }}>
                      {t('latestMeasurement')}
                    </h3>
                    {latest && !isEditingLatest && (
                      <button
                        onClick={startEditLatest}
                        style={{
                          background: '#3182ce',
                          color: 'white',
                          border: 'none',
                          padding: '5px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 500
                        }}
                      >
                        {t('edit')}
                      </button>
                    )}
                  </div>

                  {latest ? (
                    isEditingLatest ? (
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '200px' }}>
                          <label style={{ display: 'block', fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                            {t('measurementDate')}
                          </label>
                          <input
                            type="date"
                            value={editDate}
                            onChange={(e) => setEditDate(e.target.value)}
                            style={{
                              padding: '8px 10px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '14px',
                              width: '100%'
                            }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignSelf: 'flex-end' }}>
                          <button
                            onClick={handleUpdateLatest}
                            disabled={!editDate || isUpdating}
                            style={{
                              background: '#16a34a',
                              color: 'white',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '4px',
                              cursor: editDate && !isUpdating ? 'pointer' : 'not-allowed',
                              fontSize: '14px',
                              fontWeight: 500,
                              opacity: !editDate || isUpdating ? 0.6 : 1
                            }}
                          >
                            {isUpdating ? t('updating') : t('save')}
                          </button>
                          <button
                            onClick={() => handleDelete(latest!)}
                            style={{
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: 500
                            }}
                          >
                            {t('delete')}
                          </button>
                          <button
                            onClick={() => setIsEditingLatest(false)}
                            style={{
                              background: '#6b7280',
                              color: 'white',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                          >
                            {t('cancel')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>{t('measurementDate')}</div>
                          <div style={{ fontSize: '15px', fontWeight: 500, color: '#1a202c' }}>
                            {formatDateDDMMYYYY(latest.measurementDate)}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>{t('lastMeasurementDate')}</div>
                          <div style={{ fontSize: '15px', fontWeight: 500, color: '#1a202c' }}>
                            {latest.lastMeasurementDate ? formatDateDDMMYYYY(latest.lastMeasurementDate) : t('noLastMeasurement')}
                          </div>
                        </div>
                      </div>
                    )
                  ) : (
                    <div style={{ textAlign: 'center', color: '#6b7280', padding: '16px 0' }}>
                      {t('noMeasurements')}
                    </div>
                  )}
                </div>

                {/* History Section */}
                {history.length > 0 && (
                  <div>
                    <h3 style={{ margin: '0 0 16px 0', color: '#1a202c', fontSize: '16px', fontWeight: 600 }}>
                      {t('measurementHistory')}
                    </h3>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '14px'
                      }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                            <th style={{ textAlign: 'left', padding: '10px 12px', color: '#4a5568', fontWeight: 600 }}>
                              {t('measurementDate')}
                            </th>
                            <th style={{ textAlign: 'left', padding: '10px 12px', color: '#4a5568', fontWeight: 600 }}>
                              {t('lastMeasurementDate')}
                            </th>
                            <th style={{ textAlign: 'center', padding: '10px 12px', color: '#4a5568', fontWeight: 600 }}>
                              {t('actions')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {history.map((m) => (
                            <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '10px 12px' }}>
                                {formatDateDDMMYYYY(m.measurementDate)}
                              </td>
                              <td style={{ padding: '10px 12px' }}>
                                {m.lastMeasurementDate ? formatDateDDMMYYYY(m.lastMeasurementDate) : t('noLastMeasurement')}
                              </td>
                              <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                <button
                                  onClick={() => handleDelete(m)}
                                  style={{
                                    background: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    padding: '4px 10px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: 500
                                  }}
                                >
                                  {t('delete')}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={confirmation.isOpen}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText={confirmation.cancelText}
        type={confirmation.type}
        onConfirm={confirmation.onConfirm}
        onCancel={confirmation.onCancel}
      />
    </>
  );
};

export default BodyFatMeasurementsModal;
